import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { money, paymentLabel, todayIn, usePoll } from "@/lib/helpers";
import StatusBadge from "@/components/StatusBadge";
import MethodBreakdown from "@/components/MethodBreakdown";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVocab } from "@/lib/vocab";

// Facturation d'une mission (factures seulement, les devis ne comptent pas).
const BILLING = {
  paid: { label: "Payée", cls: "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400" },
  sent: { label: "Facturée · non payée", cls: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400" },
  draft: { label: "Facture en brouillon", cls: "text-muted-foreground bg-muted" },
  subscription: { label: "Abonnement", cls: "text-violet-700 bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300" },
  none: { label: "Non facturée", cls: "text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400" },
};

// Décale une date AAAA-MM-JJ de n jours (calcul en UTC : aucun effet de fuseau ni d'heure d'été).
const shiftDay = (day, n) => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const longDate = (day) => {
  const s = new Date(`${day}T12:00:00Z`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function Kpi({ icon: Icon, label, value, tint, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl shadow-card p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${tint}1a`, color: tint }}><Icon className="h-5 w-5" /></div>
        <div className="text-xs uppercase tracking-overline text-muted-foreground">{label}</div>
      </div>
      <div className="font-display font-black text-3xl mt-3 tracking-tight" style={{ color: tint }} data-testid={testid && `${testid}-value`}>{value}</div>
      {children}
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    // min-w-0 : dans la grille, l'encadré peut rétrécir ; un texte « truncate » se coupe au lieu d'élargir la page.
    <div className="bg-card border border-border rounded-2xl shadow-card min-w-0" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="text-xs uppercase tracking-overline text-muted-foreground">{title}</div>
        {count > 0 && <span className="text-xs text-muted-foreground">{count}</span>}
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

const Empty = ({ children }) => <p className="px-5 py-6 text-sm text-muted-foreground">{children}</p>;

export default function Journal() {
  const { settings } = useSettings();
  const voc = useVocab();
  const cur = settings?.currency || "EUR";
  const tz = settings?.timezone || "Europe/Monaco";
  const [day, setDay] = useState(() => todayIn(tz));
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!day) return;
    api.get("/journal", { params: { day } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [day]);
  useEffect(() => { load(); }, [load]);
  usePoll(() => load(), 30000);

  const hour = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: tz }) : "");
  const today = todayIn(tz);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-overline text-accent">Au quotidien</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-1">Journal</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="icon" variant="outline" onClick={() => setDay((d) => shiftDay(d, -1))} title="Jour précédent" data-testid="journal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-9 px-2.5 rounded-lg border border-border bg-card text-sm" data-testid="journal-day" />
          <Button size="icon" variant="outline" onClick={() => setDay((d) => shiftDay(d, 1))} title="Jour suivant" data-testid="journal-next"><ChevronRight className="h-4 w-4" /></Button>
          {day !== today && <Button variant="outline" onClick={() => setDay(today)} data-testid="journal-today">Aujourd'hui</Button>}
        </div>
      </div>

      {data && data.day === day && (
        <>
          <p className="text-sm text-muted-foreground" data-testid="journal-day-label">{longDate(day)}</p>
          {/* 2 cases côte à côte à partir de 1024 px ; en dessous, une par ligne (à 768 px, un gros montant sortait de sa case). */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Kpi icon={TrendingUp} label="CA du jour (encaissé)" value={money(data.revenue, cur)} tint="#10b981" testid="journal-revenue">
              <p className="text-sm text-muted-foreground mt-2" data-testid="journal-done">{`${voc.journalDone} : `}<span className="font-medium text-foreground">{money(data.done_total, cur)}</span>{data.done_count > 0 && ` (${data.done_count} ${data.done_count > 1 ? `${voc.missions} terminées` : `${voc.mission} terminée`})`}</p>
              {data.done_subscription_total > 0 && <p className="text-xs text-muted-foreground mt-1" data-testid="journal-done-subscription">dont {money(data.done_subscription_total, cur)} sous abonnement (déjà encaissés à la vente de l'abonnement)</p>}
              {data.advances > 0 && <p className="text-sm text-muted-foreground mt-2" data-testid="journal-advances">Avances carburant (hors CA) : <span className="font-medium text-foreground">{money(data.advances, cur)}</span></p>}
              {data.pending_count > 0 && <p className="text-xs text-muted-foreground mt-1" data-testid="journal-pending">Pas encore terminées : {data.pending_count} {data.pending_count > 1 ? voc.missions : voc.mission} · {money(data.pending_total, cur)}</p>}
            </Kpi>
            <Kpi icon={TrendingDown} label="Dépenses du jour" value={money(data.expenses_total, cur)} tint="#ef4444" testid="journal-expenses" />
          </div>
          <MethodBreakdown byMethod={data.by_method} revenue={data.revenue} cur={cur} title="Encaissé par mode de paiement" />

          <Section title={voc.journalDay} count={data.missions.length} testid="journal-missions">
            {data.missions.length === 0 && <Empty>{`Aucune ${voc.mission} ce jour-là.`}</Empty>}
            {data.missions.map((m) => {
              const cancelled = m.status === "cancelled";
              const b = BILLING[m.billing] || BILLING.none;
              return (
                <div key={m.id} className="px-5 py-3 flex items-start gap-3 flex-wrap" data-testid={`journal-mission-${m.id}`}>
                  <div className="w-12 shrink-0 text-sm font-medium tabular-nums pt-0.5">{hour(m.start)}</div>
                  <div className="flex-1 min-w-[160px]">
                    <div className={cn("font-semibold", cancelled && "text-muted-foreground")}>{m.client_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{[m.type, m.vehicle, m.employee_name].filter(Boolean).join(" · ")}</div>
                  </div>
                  {/* Sur téléphone : pastilles sous le texte (alignées avec lui), prix à droite. */}
                  <div className="flex items-center gap-2 flex-wrap justify-end ml-auto max-md:basis-full max-md:justify-start max-md:pl-[3.75rem]">
                    <StatusBadge status={m.status} />
                    {/* Course où le client était absent (transport, lot T4a) : réalisée et facturée, avec la mention. */}
                    {m.ride_step === "no_show" && <span className="px-2.5 py-1 text-xs font-medium rounded-full text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300" data-testid={`journal-no-show-${m.id}`}>Client absent</span>}
                    {!cancelled && <span className={cn("px-2.5 py-1 text-xs font-medium rounded-full", b.cls)} data-testid={`journal-billing-${m.id}`}>{b.label}{m.invoice_number ? ` · ${m.invoice_number}` : ""}{m.billing === "subscription" && m.subscription_holder ? ` · ${m.subscription_holder}` : ""}</span>}
                    <span className={cn("font-display font-bold min-w-24 text-right max-md:ml-auto", cancelled && "line-through text-muted-foreground font-normal")}>{money(m.price_ttc, cur)}</span>
                  </div>
                </div>
              );
            })}
          </Section>

          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Encaissements du jour" count={data.payments.length} testid="journal-payments">
              {data.payments.length === 0 && <Empty>Aucune facture payée ce jour-là.</Empty>}
              {data.payments.map((p) => (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`journal-payment-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.client_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{p.number} · {p.payment_method ? paymentLabel(p.payment_method) : "Mode non renseigné"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {/* Montant hors avance carburant (le CA) ; l'avance encaissée est rappelée dessous. */}
                    <div className="font-display font-bold" data-testid={`journal-payment-amount-${p.id}`}>{money(Math.round((p.total_ttc - (p.total_debours || 0)) * 100) / 100, cur)}</div>
                    {p.total_debours > 0 && <div className="text-xs text-muted-foreground" data-testid={`journal-payment-advance-${p.id}`}>+ {money(p.total_debours, cur)} d'avance carburant</div>}
                  </div>
                </div>
              ))}
            </Section>
            <Section title="Dépenses du jour" count={data.expenses.length} testid="journal-expense-list">
              {data.expenses.length === 0 && <Empty>Aucune dépense ce jour-là.</Empty>}
              {data.expenses.map((e) => (
                <div key={e.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`journal-expense-${e.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.label}</div>
                    {e.category && <div className="text-xs text-muted-foreground">{e.category}</div>}
                  </div>
                  <span className="font-display font-bold shrink-0 text-red-600">{money(e.amount, cur)}</span>
                </div>
              ))}
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
